import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';

import { tokens, Link } from '@hotjar/hotsauce';
import { mapDtoFieldKeysToModelKeys } from '@hotjar/webapp/domains/registration/mapper';
import { SsoErrorMessage } from '@hotjar/webapp/features/Activation/SsoError/SsoErrorMessage';
import { useSsoError } from '@hotjar/webapp/features/Activation/SsoError/useSsoError';

import { useUserLogin } from '@hotjar/auth/src/domains/authentication/useUserLogin';
import { useRoleChoices } from '@hotjar/webapp/hooks/useRoleChoices';
import { SamlSsoButton } from '../SamlSsoButton';
import { useInvitationSamlSsoApi, STATUS } from './useInvitationSamlSsoApi';

import {
  errorMessageStyles,
  Label,
  StyledInput as Input,
  PageTitle,
  PageSubTitle,
  ReadOnlyData,
  ReadOnlyDataIcon,
  FormRow,
  ButtonRow,
  StyledSelect as Select,
  StyledCheckbox,
  TitleSection,
} from './styles';

import { TERMS_LINK } from './constants';

export interface InvitationSamlSsoProps {
  email: string;
  organizationName: string;
  invitationToken: string;
  // FIXME: Use this prop or remove it if not needed.
  // eslint-disable-next-line react/no-unused-prop-types
  identityProvider: {
    id: string;
    displayName: string;
  };
}

interface FormResult {
  email: string;
  organizationName: string;
  name: string;
  identityProvider: {
    id: string;
    displayName: string;
  };
  role: string;
  terms: boolean;
}

export const InvitationSamlSso = ({
  email,
  organizationName,
  invitationToken,
}: InvitationSamlSsoProps): JSX.Element => {
  const { t } = useTranslation();

  const {
    data: apiResponseData,
    apiErrorMessage,
    status: apiStatus,
    fieldErrors: apiErrors,
    registerSamlSsoUser: apiRegister,
  } = useInvitationSamlSsoApi();
  const { logoutAndClearData } = useUserLogin();
  const roles = useRoleChoices();
  const { code, apiErrorMessageKey } = useSsoError(apiErrorMessage);
  const { control, register, handleSubmit, errors, formState, setError } = useForm<FormResult>({
    mode: 'onBlur',
  });
  const { isSubmitting } = formState;

  useEffect(() => {
    if (apiResponseData?.nextUrl) {
      window.location.href = apiResponseData?.nextUrl;
    }
  }, [apiResponseData]);

  useEffect(() => {
    if (!apiErrors?.errors) {
      return;
    }

    Object.entries(apiErrors.errors).forEach((error) => {
      const [key, value] = error;
      const [firstValidationMessage] = value;
      const modelKey = mapDtoFieldKeysToModelKeys(key);
      if (!modelKey) {
        return;
      }
      setError(modelKey, {
        type: 'manual',
        message: firstValidationMessage,
      });
    });
  }, [apiErrors, setError]);

  const onSubmit = (formData: FormResult) => {
    logoutAndClearData(() =>
      apiRegister({
        name: formData.name,
        role: formData.role,
        terms: formData.terms,
        invitationToken,
      }),
    );
  };

  const isDisabled: boolean =
    isSubmitting || apiStatus === STATUS.LOADING || apiStatus === STATUS.SUCCESS;

  return (
    <div data-testid="saml-sso-signup-form">
      <div className="col-form">
        <div className="inner">
          <TitleSection>
            <PageTitle data-testid="sso-title">{t('activation:saml-sso.page-title')}</PageTitle>
            <PageSubTitle data-testid="sso-secondary-title">
              {t('activation:saml-sso.secondary-title')}
            </PageSubTitle>
          </TitleSection>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Error Code error */}
            <SsoErrorMessage errorCode={code} />
            {/* Api Error */}
            <SsoErrorMessage errorCode={apiErrorMessageKey} />

            <div>
              <FormRow>
                <Label>{t('activation:saml-sso.form.label-team')}</Label>
                <ReadOnlyData>
                  <ReadOnlyDataIcon icon="check" size={16} color={tokens.color.mint_600} />
                  {organizationName}
                </ReadOnlyData>
              </FormRow>

              <FormRow>
                <Label>{t('activation:saml-sso.form.label-email')}</Label>
                <ReadOnlyData>
                  <ReadOnlyDataIcon icon="check" size={16} color={tokens.color.mint_600} />
                  {email}
                </ReadOnlyData>
              </FormRow>

              <FormRow>
                <Label htmlFor="saml-sso-name">{t('activation:saml-sso.form.label-name')}</Label>
                <Input
                  id="saml-sso-name"
                  aria-label="saml-sso-name"
                  size="l"
                  name="name"
                  placeholder={t('activation:saml-sso.form.placeholder-name')}
                  inputRef={register({ required: true })}
                  errorMessage={
                    (!!errors.name && errors.name.message) ||
                    t('activation:saml-sso.form.error-message-required-field')
                  }
                  isError={!!errors.name}
                  disabled={isDisabled}
                />
              </FormRow>

              <FormRow>
                <Label htmlFor="sso-role">{t('activation:saml-sso.form.label-role')}</Label>
                <Select
                  id="sso-role"
                  aria-label="saml-sso-role"
                  name="role"
                  size="l"
                  ref={register({ required: true })}
                  isError={!!errors.role}
                  errorMessage={
                    (!!errors.role && errors.role.message) ||
                    t('activation:saml-sso.form.error-message-required-field')
                  }
                  disabled={isDisabled}
                >
                  <option value="">{t('activation:saml-sso.form.option-default-role')}</option>
                  {roles.map(({ value, label }) => (
                    <option value={value} key={value}>
                      {t(label)}
                    </option>
                  ))}
                </Select>
              </FormRow>

              <FormRow>
                <Controller
                  control={control}
                  name="terms"
                  defaultValue={false}
                  rules={{ required: true }}
                  render={({ onChange, value }) => (
                    <div>
                      <StyledCheckbox
                        partial={false}
                        onChange={(e) => onChange(e.target.checked)}
                        checked={value}
                        disabled={isDisabled}
                      >
                        <Trans i18nKey="activation:saml-sso.form.terms-condition">
                          <span data-testid="saml-sso-checkbox-label">I accept the</span>
                          <Link href={TERMS_LINK} kind="inline" isExternal>
                            Terms of Service
                          </Link>
                        </Trans>
                      </StyledCheckbox>

                      {errors.terms && (
                        <div css={errorMessageStyles}>
                          {(!!errors.terms && errors.terms.message) ||
                            t('activation:saml-sso.form.error-message-required-field')}
                        </div>
                      )}
                    </div>
                  )}
                />
              </FormRow>

              <ButtonRow>
                <SamlSsoButton
                  type="submit"
                  size="large"
                  disabled={isDisabled}
                  secondary={false}
                  data-testid="saml-sso-submit-button"
                >
                  {t('activation:saml-sso.form.submit-button')}
                </SamlSsoButton>
              </ButtonRow>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
